// Help Q&A client (typed help/intake framework, commit 5).
//
// Sibling of supportTicketApi.js / requestIntakeApi.js, sharing their idempotency
// helper rather than inventing a third one.
//
// Two things this module deliberately does NOT do:
//
//   - It does not decide whether an answer was good enough. The server returns
//     `decision: { deflected, reasons }` and the page renders it. There is no
//     client-side confidence rule here, and there must never be one.
//   - It does not have a question-only lifecycle. Adding context, preparing, and
//     submitting a question all go through the SAME typed-intake endpoints the other
//     verticals use, so ownership, revision, readiness, materialization, and audit
//     have exactly one implementation.

import { api } from '../../lib/apiClient.js';

import { newIdempotencyKey } from '../feedbackIntake/supportTicketApi.js';

const INTAKE_BASE = '/feedback/intakes';
const ASK_PATH = '/help/questions/ask';

export { newIdempotencyKey };

function intakePath(intakeId, suffix = '') {
  return `${INTAKE_BASE}/${encodeURIComponent(intakeId)}${suffix}`;
}

/**
 * Ask PRIME a question.
 *
 * Omit `intakeId` for the first ask — the server creates the question intake in the
 * same transaction as the answer. Supply it (with `expectedRevision`) to continue.
 *
 * A UUID Idempotency-Key is REQUIRED: this call retrieves from the knowledge base
 * and may invoke a model, so a retry after an ambiguous failure must replay the
 * original answer and decision rather than spend a second retrieval and append a
 * second pair of turns.
 */
export async function askQuestion(
  { intakeId = null, expectedRevision = null, question, sourceRoute = null } = {},
  { idempotencyKey, signal } = {},
) {
  return api.post(
    ASK_PATH,
    {
      ...(intakeId ? { intakeId, expectedRevision } : {}),
      question,
      ...(sourceRoute ? { sourceRoute } : {}),
    },
    {
      headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() },
      // The reporter can stop waiting for a slow search; the HTTP wait is genuinely
      // dropped, and the same key resumes or replays it.
      ...(signal ? { signal } : {}),
    },
  );
}

export async function getQuestionIntake(intakeId) {
  return api.get(intakePath(intakeId));
}

export async function patchQuestionIntake(intakeId, payload) {
  return api.patch(intakePath(intakeId), payload);
}

export async function prepareQuestionIntake(intakeId, expectedRevision) {
  return api.post(intakePath(intakeId, '/prepare'), { expectedRevision });
}

export async function submitQuestionIntake(intakeId, expectedRevision, { idempotencyKey } = {}) {
  return api.post(
    intakePath(intakeId, '/submit'),
    { expectedRevision },
    { headers: { 'Idempotency-Key': idempotencyKey || newIdempotencyKey() } },
  );
}

export async function cancelQuestionIntake(intakeId, expectedRevision, reason = null) {
  return api.post(intakePath(intakeId, '/cancel'), { expectedRevision, reason });
}
